/**
 * The Productgegevens screen, decided outside React.
 *
 * `productSync.js` answers "what would copying this do"; this file answers the questions the
 * screen itself has: which rows are on the table, how many of them still have no warehouse
 * data, what a stored value looks like written out, and what is left of a sync plan after the
 * user unticks one of its lines.
 *
 * Pure on purpose - the table is the first place anyone will ever be able to see how much of
 * the freezer is actually filled in, so the counting has to be testable rather than inlined in
 * a `useMemo`.
 */

import { getSyncableProperty, WAREHOUSE_FIELDS } from "../constants/ProductSyncConstant";
import { normalizeProductName } from "./pickRoute";
import {
  compactProductName,
  findLinkForProduct,
  linkCounterpart,
  normaliseGrams,
  SHOP_VCE,
} from "./productSync";

/** What an unset value reads as in the melding, so an empty cell is never just blank. */
export const EMPTY_VALUE_LABEL = "(leeg)";

/** The filter values of the shop selector; "all" is not a shop. */
export const SHOP_FILTER_ALL = "all";

/** The three warehouse fields as the form wants them: every key present, missing means null. */
export const warehouseValuesOf = (product) =>
  WAREHOUSE_FIELDS.reduce(
    (values, key) => ({
      ...values,
      [key]: !product || product[key] === undefined ? null : product[key],
    }),
    {},
  );

/**
 * Whether this product has the data the freezer needs.
 *
 * Both gram values, and only those: the pick weight is what the packing slip states and what
 * box-size prediction reasons about, the volume weight is what decides which box it goes in.
 * A pick note is advice for the picker - a product without one is not incomplete, and counting
 * it as such would put half the catalogue on the "nog te doen" pile forever.
 */
export const hasWarehouseData = (product) =>
  normaliseGrams(product && product.pickWeightGram) !== null &&
  normaliseGrams(product && product.volumeWeightGram) !== null;

/**
 * One row per product of both shops, with its link already resolved.
 *
 * Gram values are normalised here, so the table sorts on numbers even though Firestore holds
 * some of them as strings - "900" must not sort before "1200".
 */
export const buildProductRows = ({ products, links }) =>
  (products || [])
    .filter((product) => !!product && !!product.id)
    .map((product) => {
      const shopSource = product.shopSource || SHOP_VCE;
      const link = findLinkForProduct(links, shopSource, product.id);
      return {
        key: `${shopSource}|${product.id}`,
        id: product.id,
        shopSource,
        product,
        name: product.name || "",
        pickWeightGram: normaliseGrams(product.pickWeightGram),
        volumeWeightGram: normaliseGrams(product.volumeWeightGram),
        pickNote: product.pickNote || null,
        updatedAt: product.updatedAt || null,
        updatedBy: product.updatedBy || null,
        link: link || null,
        counterpart: !link ? null : linkCounterpart(link, shopSource),
        isLinked: !!link,
        isComplete: hasWarehouseData(product),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, "nl") || a.shopSource.localeCompare(b.shopSource));

/**
 * The filters staff actually need: find a product, look at one shop, and above all see what is
 * still empty - the first round through this screen is a catch-up, not maintenance.
 *
 * Searching also matches the linked product's name, so the pair is found under either spelling.
 * Every word typed has to occur somewhere in the name, rather than the whole string in one
 * piece: the two shops write the same product as "Kipnuggets 1 kg" and "Kip nuggets 1kg", and a
 * search that only matches one of those spellings is a search nobody trusts.
 *
 * Which is also why the name is searched with its spaces removed: someone typing "kipnuggets"
 * is looking for both of those products, and would otherwise be told one of them does not
 * exist. The words typed are matched against that same compacted name, so word order and the
 * spacing of either spelling stop mattering.
 */
export const filterProductRows = (rows, { search, shop, onlyMissing, onlyUnlinked } = {}) => {
  const needles = normalizeProductName(search || "")
    .split(" ")
    .filter((word) => !!word);

  return (rows || []).filter((row) => {
    if (!!shop && shop !== SHOP_FILTER_ALL && row.shopSource !== shop) return false;
    if (onlyMissing && row.isComplete) return false;
    if (onlyUnlinked && row.isLinked) return false;
    if (needles.length === 0) return true;

    // Compacted per name, not over the two of them joined: gluing the names together would
    // invent matches that straddle the boundary between them.
    const haystack = [row.name, row.counterpart && row.counterpart.name]
      .filter((part) => !!part)
      .map(compactProductName)
      .join(" ");
    return needles.every((word) => haystack.includes(word));
  });
};

const emptyShopSummary = () => ({ total: 0, linked: 0, missing: 0 });

/**
 * How many products each shop has, how many are linked and how many still have no warehouse
 * data. Nobody has ever measured this - it is the number that says how far the catch-up is.
 */
export const summariseProductRows = (rows) => {
  const summary = { total: 0, linked: 0, missing: 0, shops: {} };

  (rows || []).forEach((row) => {
    if (!summary.shops[row.shopSource]) summary.shops[row.shopSource] = emptyShopSummary();
    const shop = summary.shops[row.shopSource];

    summary.total += 1;
    shop.total += 1;
    if (row.isLinked) {
      summary.linked += 1;
      shop.linked += 1;
    }
    if (!row.isComplete) {
      summary.missing += 1;
      shop.missing += 1;
    }
  });

  return summary;
};

/** Grams as the warehouse writes them: "2.400 g", or "(leeg)" when there is no value. */
export const formatGrams = (value) => {
  const grams = normaliseGrams(value);
  if (grams === null) return EMPTY_VALUE_LABEL;
  return `${grams.toLocaleString("nl-NL")} g`;
};

/**
 * A stored value written out for the melding, per property type.
 *
 * The prompt is a statement about two concrete products - "Pickgewicht: 2400 g -> 3000 g" - so
 * every value has to render as something a human recognises, including the ones the registry
 * carries for later phases (lists, maps, booleans).
 */
export const formatSyncValue = (key, value) => {
  const property = getSyncableProperty(key);
  const type = (property && property.type) || "text";

  if (type === "grams") return formatGrams(value);
  if (value === null || value === undefined || value === "") return EMPTY_VALUE_LABEL;
  if (type === "boolean") return value ? "Ja" : "Nee";

  if (type === "list") {
    if (!Array.isArray(value) || value.length === 0) return EMPTY_VALUE_LABEL;
    return value
      .map((item) => (item && typeof item === "object" ? Object.values(item).join(" ") : `${item}`))
      .join(", ");
  }

  if (type === "map") {
    if (typeof value !== "object" || Array.isArray(value)) return `${value}`;
    const entries = Object.keys(value);
    if (entries.length === 0) return EMPTY_VALUE_LABEL;
    return entries.map((entryKey) => `${entryKey}: ${value[entryKey]}`).join(", ");
  }

  return `${value}`;
};

/**
 * The plan that is left after the user unticks lines in the melding.
 *
 * The prompt lists every change, but the user decides per line - "de inhoud wel, de picknotitie
 * niet". Narrowing the plan rather than building a second one keeps `applySyncPlan` seeing
 * exactly the shape it expects, including the `from` values its stale check depends on.
 */
export const narrowSyncPlan = (plan, keys) => {
  if (!plan) return null;

  const wanted = new Set(keys || []);
  const changes = (plan.changes || []).filter((change) => wanted.has(change.key));

  return {
    ...plan,
    changes,
    hasChanges: changes.length > 0,
    values: changes.reduce((values, change) => ({ ...values, [change.key]: change.to }), {}),
  };
};

/**
 * Properties the user just changed that this link is deliberately not copying (price, by
 * default). Worth saying out loud: otherwise "ik heb de prijs aangepast en er gebeurde niets
 * aan de andere kant" looks like a bug instead of a setting.
 */
export const notSelectedProperties = (plan) =>
  ((plan && plan.skipped) || []).filter((entry) => entry.reason === "not-selected");
